import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, get, FieldError } from 'react-hook-form';
import { formatDynamicErrorMessage } from '@anytag/shared-utils';
import { ErrorMessage, Form, Label, TextCutter } from '@anytag/shared-atoms';
import { DefaultFieldProps } from '../types';

export interface RHFFieldWrapperProps<T extends string, V = string>
  extends Partial<Omit<DefaultFieldProps<T, V>, 'onChange' | 'onSubmit'>>,
    Pick<React.ComponentProps<typeof Form.Field>, 'onMouseLeave' | 'onMouseEnter'> {
  hideError?: boolean; // TODO: Currently selector has different flows a bit. Will update after refactor those components
  children: ReactElement | ReactElement[] | ((props: { hasError?: boolean; errorMessage?: string }) => ReactNode);
}

export const RHFFieldWrapper = <T extends string, V = string>({
  name,
  help,
  note,
  title,
  required,
  optional,
  children,
  className,
  hideError,
  customError,
  labelLoader,
  dynamicError,
  onMouseEnter,
  onMouseLeave
}: RHFFieldWrapperProps<T, V>) => {
  const { t } = useTranslation();
  const {
    formState: { errors }
  } = useFormContext<Record<string, string>>();

  const error = (() => {
    let message;

    if (name) {
      message = (get(errors, name) as FieldError)?.message;
    }

    return message ? (dynamicError ? formatDynamicErrorMessage(t, message) : t<string>(message)) : message;
  })();
  const hasError = !!error;
  const errorMessage = hasError ? customError || error : undefined;

  return (
    <Form.Field {...{ className, onMouseEnter, onMouseLeave }}>
      {!!title && <Label {...{ help, title, required, optional, labelLoader }} />}
      {note && <TextCutter text={note} className="mb-1 text-sm text-red-70" lines={1} />}
      {(() => {
        if (typeof children === 'function') {
          return children({ hasError, errorMessage });
        }
        if (React.isValidElement(children)) {
          return React.cloneElement(children as React.ReactElement<{ hasError?: boolean }>, { hasError });
        }

        return children;
      })()}
      {!hideError && <ErrorMessage message={errorMessage} />}
    </Form.Field>
  );
};
